import {
  CRAFT_BLOCK_TYPE,
  CustomBlockDefinition,
  LibraryBlockDefinition,
  LibraryBlockViewSnapshot,
  type BlockNoiseParams,
  customBlockId,
  libraryBlockId,
  TEXTURE_GRID_SIZE,
  thumbnailViewId,
} from '@shared/protocol';
import {
  applyBlockNoiseRgb,
  applyNoiseToTexturePixels,
  isNoiseActive,
  resolveNoiseSeed,
} from '@shared/blockNoise';
import { blockTypeById } from '../world/blocks';
import { createCraftTextureCanvas } from '../world/CraftBlockRenderer';
import { microVoxelIndex, unpackMicroVoxel } from '../world/MicroVoxelGeometry';

type Point = [number, number];
type Rgba = [number, number, number, number];

const TOP: Point[] = [[36, 5], [65, 20], [36, 36], [7, 20]];
const LEFT: Point[] = [[7, 20], [36, 36], [36, 67], [7, 51]];
const RIGHT: Point[] = [[36, 36], [65, 20], [65, 51], [36, 67]];

/** Render an isometric inventory preview without creating a Three.js scene. */
export function createBlockIcon(
  type: number,
  customBlocks: ReadonlyMap<number, CustomBlockDefinition>,
  libraryBlocks: ReadonlyMap<number, LibraryBlockDefinition> = new Map(),
): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.className = 'block-icon';
  canvas.width = canvas.height = 72;
  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingEnabled = false;
  if (type === 0) return canvas;

  const customId = customBlockId(type);
  const definition = customId === null ? null : customBlocks.get(customId);
  const libraryId = libraryBlockId(type);
  const library = libraryId === null ? null : libraryBlocks.get(libraryId);
  if (definition) {
    drawVoxelDefinition(ctx, appearanceForCustomIcon(definition));
  } else if (library) {
    const appearance = appearanceForLibraryIcon(library);
    if (appearance.kind === 'solid') {
      drawIsometricCube(
        ctx,
        cssToRgba(`${appearance.color}${alphaHex(appearance.opacity)}`),
      );
    } else {
      const pixels = isNoiseActive(appearance.noise)
        ? applyNoiseToTexturePixels(
          appearance.pixels,
          appearance.noise,
          resolveNoiseSeed(appearance.noise, appearance.seedKey),
        )
        : appearance.pixels;
      drawTextureFace(ctx, textureCanvas(pixels[4]!), LEFT);
      drawTextureFace(ctx, textureCanvas(pixels[0]!), RIGHT);
      drawTextureFace(ctx, textureCanvas(pixels[2]!), TOP);
      outlineFaces(ctx, [LEFT, RIGHT, TOP], 1.25);
    }
  } else if (type === CRAFT_BLOCK_TYPE) {
    drawCraftBlock(ctx);
  } else {
    drawIsometricCube(ctx, cssToRgba(blockTypeById(type).css));
  }
  return canvas;
}

/** Library look for icons/previews: first sequence frame when Animation is active. */
export function appearanceForLibraryIcon(library: LibraryBlockDefinition): {
  kind: 'solid';
  color: string;
  opacity: number;
} | {
  kind: 'textured';
  pixels: string[][];
  noise: BlockNoiseParams | null;
  seedKey: number;
} {
  const snapId = thumbnailViewId(library.activeViewId, library.animation);
  const snapshot = snapId
    ? library.snapshots?.find((item) => item.id === snapId)
    : undefined;
  if (snapshot) return appearanceFromLibrarySnapshot(snapshot, library.id);
  if (library.kind === 'solid') {
    return { kind: 'solid', color: library.color, opacity: library.opacity };
  }
  return {
    kind: 'textured',
    pixels: library.pixels,
    noise: library.noise,
    seedKey: library.id,
  };
}

/** Custom look for icons/previews: first sequence frame when Animation is active. */
export function appearanceForCustomIcon(definition: CustomBlockDefinition): {
  id: number;
  gridSize: number;
  voxels: CustomBlockDefinition['voxels'];
  noise: CustomBlockDefinition['noise'];
} {
  const snapId = thumbnailViewId(definition.activeViewId, definition.animation);
  const snapshot = snapId
    ? definition.snapshots?.find((item) => item.id === snapId)
    : undefined;
  if (snapshot) {
    return {
      id: definition.id,
      gridSize: snapshot.gridSize,
      voxels: snapshot.voxels,
      noise: snapshot.noise,
    };
  }
  return {
    id: definition.id,
    gridSize: definition.gridSize,
    voxels: definition.voxels,
    noise: definition.noise,
  };
}

function appearanceFromLibrarySnapshot(
  snapshot: LibraryBlockViewSnapshot,
  fallbackSeed: number,
): ReturnType<typeof appearanceForLibraryIcon> {
  if (snapshot.kind === 'solid') {
    return {
      kind: 'solid',
      color: snapshot.color,
      opacity: snapshot.opacity,
    };
  }
  return {
    kind: 'textured',
    pixels: snapshot.pixels,
    noise: snapshot.noise,
    seedKey: snapshot.id || fallbackSeed,
  };
}

function textureCanvas(pixels: string[]): HTMLCanvasElement {
  const size = Math.round(Math.sqrt(pixels.length)) || TEXTURE_GRID_SIZE;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  pixels.forEach((color, index) => {
    ctx.fillStyle = color;
    ctx.fillRect(
      index % size,
      size - 1 - Math.floor(index / size),
      1,
      1,
    );
  });
  return canvas;
}

function drawIsometricCube(ctx: CanvasRenderingContext2D, color: Rgba): void {
  polygon(ctx, LEFT, shade(color, 0.68));
  polygon(ctx, RIGHT, shade(color, 0.84));
  polygon(ctx, TOP, shade(color, 1.12));
}

function drawCraftBlock(ctx: CanvasRenderingContext2D): void {
  drawTextureFace(ctx, createCraftTextureCanvas('left'), LEFT);
  drawTextureFace(ctx, createCraftTextureCanvas('front'), RIGHT);
  drawTextureFace(ctx, createCraftTextureCanvas('top'), TOP);
  outlineFaces(ctx, [LEFT, RIGHT, TOP], 1.25);
}

function drawTextureFace(
  ctx: CanvasRenderingContext2D,
  texture: HTMLCanvasElement,
  face: Point[],
): void {
  const [origin, xCorner, , yCorner] = face;
  ctx.save();
  path(ctx, face);
  ctx.clip();
  ctx.setTransform(
    (xCorner[0] - origin[0]) / texture.width,
    (xCorner[1] - origin[1]) / texture.width,
    (yCorner[0] - origin[0]) / texture.height,
    (yCorner[1] - origin[1]) / texture.height,
    origin[0],
    origin[1],
  );
  ctx.drawImage(texture, 0, 0);
  ctx.restore();
}

function drawVoxelDefinition(
  ctx: CanvasRenderingContext2D,
  definition: {
    id: number;
    gridSize: number;
    voxels: CustomBlockDefinition['voxels'];
    noise: CustomBlockDefinition['noise'];
  },
): void {
  const gridSize = definition.gridSize;
  const cells = new Map(definition.voxels);
  if (cells.size === 0) return;
  const hasCell = (x: number, y: number, z: number): boolean =>
    x >= 0 && x < gridSize
    && y >= 0 && y < gridSize
    && z >= 0 && z < gridSize
    && cells.has(microVoxelIndex(x, y, z, gridSize));

  const voxels = [...cells.entries()].map(([index, type]) => {
    const [x, y, z] = unpackMicroVoxel(index, gridSize);
    return { x, y, z, type };
  });
  const bounds = projectedBounds(voxels);
  const width = Math.max(1, bounds.maxX - bounds.minX);
  const height = Math.max(1, bounds.maxY - bounds.minY);
  const scale = Math.min(60 / width, 60 / height);
  const offsetX = 36 - ((bounds.minX + bounds.maxX) * scale) / 2;
  const offsetY = 36 - ((bounds.minY + bounds.maxY) * scale) / 2;
  const project = (x: number, y: number, z: number): Point => [
    (x - z) * scale + offsetX,
    ((x + z) * 0.52 - y) * scale + offsetY,
  ];

  voxels.sort((a, b) =>
    (a.x + a.y + a.z) - (b.x + b.y + b.z)
    || a.y - b.y
    || a.z - b.z
    || a.x - b.x);

  for (const voxel of voxels) {
    const { x, y, z, type } = voxel;
    let color = cssToRgba(
      typeof type === 'number' ? blockTypeById(type).css : type,
    );
    if (isNoiseActive(definition.noise)) {
      const [r, g, b] = applyBlockNoiseRgb(
        color[0] / 255,
        color[1] / 255,
        color[2] / 255,
        x,
        y,
        z,
        gridSize,
        definition.noise!,
        resolveNoiseSeed(definition.noise, definition.id),
      );
      color = [
        Math.round(r * 255),
        Math.round(g * 255),
        Math.round(b * 255),
        color[3],
      ];
    }
    if (!hasCell(x, y, z + 1)) {
      polygon(ctx, [
        project(x, y, z + 1),
        project(x, y + 1, z + 1),
        project(x + 1, y + 1, z + 1),
        project(x + 1, y, z + 1),
      ], shade(color, 0.68), 0.35);
    }
    if (!hasCell(x + 1, y, z)) {
      polygon(ctx, [
        project(x + 1, y + 1, z + 1),
        project(x + 1, y + 1, z),
        project(x + 1, y, z),
        project(x + 1, y, z + 1),
      ], shade(color, 0.84), 0.35);
    }
    if (!hasCell(x, y + 1, z)) {
      polygon(ctx, [
        project(x, y + 1, z),
        project(x + 1, y + 1, z),
        project(x + 1, y + 1, z + 1),
        project(x, y + 1, z + 1),
      ], shade(color, 1.12), 0.35);
    }
  }
}

function projectedBounds(
  voxels: Array<{ x: number; y: number; z: number }>,
): { minX: number; maxX: number; minY: number; maxY: number } {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const { x, y, z } of voxels) {
    for (const dx of [0, 1]) for (const dy of [0, 1]) for (const dz of [0, 1]) {
      const px = x + dx - (z + dz);
      const py = ((x + dx + z + dz) * 0.52) - (y + dy);
      minX = Math.min(minX, px);
      maxX = Math.max(maxX, px);
      minY = Math.min(minY, py);
      maxY = Math.max(maxY, py);
    }
  }
  return { minX, maxX, minY, maxY };
}

function polygon(
  ctx: CanvasRenderingContext2D,
  points: Point[],
  fill: string,
  lineWidth = 1.25,
): void {
  path(ctx, points);
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.strokeStyle = 'rgba(18,18,18,0.68)';
  ctx.lineWidth = lineWidth;
  ctx.stroke();
}

function path(ctx: CanvasRenderingContext2D, points: Point[]): void {
  ctx.beginPath();
  ctx.moveTo(...points[0]);
  for (let index = 1; index < points.length; index++) ctx.lineTo(...points[index]);
  ctx.closePath();
}

function outlineFaces(
  ctx: CanvasRenderingContext2D,
  faces: Point[][],
  lineWidth: number,
): void {
  ctx.strokeStyle = 'rgba(18,18,18,0.75)';
  ctx.lineWidth = lineWidth;
  for (const face of faces) {
    path(ctx, face);
    ctx.stroke();
  }
}

function cssToRgba(css: string): Rgba {
  const hex = css.slice(1);
  const value = Number.parseInt(hex, 16);
  return hex.length === 8
    ? [(value >>> 24) & 255, (value >>> 16) & 255, (value >>> 8) & 255, value & 255]
    : [(value >> 16) & 255, (value >> 8) & 255, value & 255, 255];
}

function shade(color: Rgba, amount: number): string {
  const values = color.slice(0, 3).map((component) =>
    Math.max(0, Math.min(255, Math.round(component * amount))));
  return `rgba(${values[0]},${values[1]},${values[2]},${color[3] / 255})`;
}

function alphaHex(opacity: number): string {
  return Math.round(Math.max(0, Math.min(1, opacity)) * 255)
    .toString(16)
    .padStart(2, '0');
}
